import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useOutlet, useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { AssetList } from '~/components/assets/AssetList';
import {
  BulkAssetEditView,
  MobileBulkAssetFAB,
} from '~/components/assets/BulkAssetEditView';
import { useAssets } from '~/components/AssetSelect';
import { Split } from '~/components/Split';
import { AddButton } from '~/components/ui/buttons/AddButton';
import { EmptyListActions } from '~/components/ui/EmptyListActions';
import { Filter } from '~/components/ui/FilterGroup';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { Loading } from '~/components/ui/Loading';
import { Checkbox, CheckboxState } from '~/components/ui/nucleus/Checkbox';
import { ListAssetsQuery, useInactiveAssetsQuery } from '~/generated/graphql';
import { Status } from '~/gql/graphql';
import { remove, unique } from '~/helpers/array';
import { extractAttributeFilterOptions } from '~/helpers/attributes';
import parseFilters from '~/helpers/parseFilters';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useSearch } from '~/hooks/useSearch';
import { useSort } from '~/hooks/useSort';
import { AssetsNav } from '~/layouts/nav/AssetsNav';
import { useContacts } from '~/routes/resources/contacts';
export type Asset = ListAssetsQuery['assets'][number];

type TAssetContext = {
  assets: Asset[];
  reload: () => void;
  onAssetDeactivated: (assetId: string) => void;
};

const noop = () => {};

const AssetContext = createContext<TAssetContext>({
  assets: [],
  reload: noop,
  onAssetDeactivated: noop,
});

export const useAssetContext = () => useContext(AssetContext);

export const useAsset = () => {
  const { assets } = useAssetContext();
  const { assetId } = useParams();
  invariant(assetId, 'useAsset requires an assetId');
  return assets.find((asset: Asset) => asset.id === assetId);
};

const SEARCH_OPTIONS = {
  keys: [
    'name',
    'displayAssetModelType',
    'displayAssetModel',
    'status',
    'deployedTo',
    'deployment.location.name',
    'deployment.space.name',
  ],
};

// Extract sort options constant to prevent recreation
const SORT_OPTIONS = { default: 'name' };

type AssetFiltersProps = {
  data: Asset[];
  onSubmit: (values: {
    query: string;
    filters: ReturnType<typeof parseFilters>;
  }) => void;
  checkboxState: CheckboxState;
  onCheckboxChange: (state: CheckboxState) => void;
  isMobile: boolean;
};

const AssetFilters = ({
  data,
  onSubmit,
  checkboxState,
  onCheckboxChange,
  isMobile,
}: AssetFiltersProps) => {
  const { t } = useTranslation();
  const contacts = useContacts();
  const [searchValue, setSearchValue] = useState('');
  const [filtersValue, setFiltersValue] = useState(new URLSearchParams());

  const options = useMemo(() => {
    return {
      type: unique(data.map((asset) => asset.type)).map((value) => ({
        value,
        label: value,
      })),
    };
  }, [data]);

  const attributeOptions = useMemo(
    () => extractAttributeFilterOptions(data, contacts),
    [data, contacts]
  );

  const deploymentOptions = useMemo(() => {
    const options = {
      locationNames: [] as string[],
      spaceNames: [] as string[],
    };
    data.forEach((asset) => {
      const deployment = asset.deployment;
      if (deployment) {
        options.locationNames.push(deployment.location.name);
        options.spaceNames.push(deployment.space.name);
      }
    });
    return options;
  }, [data]);

  // Memoize the filters array to prevent recreation on every render
  const filters: Filter[] = useMemo(
    () => [
      {
        name: 'deploymentLocation',
        label: t('place'),
        options: unique(deploymentOptions.locationNames).map((value) => ({
          value,
          label: value,
        })),
        searchable: true,
        type: 'select',
      },
      {
        name: 'deploymentSpace',
        label: t('space_plural'),
        options: unique(deploymentOptions.spaceNames).map((value) => ({
          value,
          label: value,
        })),
        searchable: true,
        type: 'select',
      },
      'divider',
      {
        name: 'status',
        label: t('status'),
        options: Object.values(Status).map((value) => ({
          value,
          label: t(`statuses.${value}`),
        })),
        searchable: false,
        type: 'select',
      },
      {
        name: 'attributes',
        label: t('attribute_plural'),
        options: attributeOptions,
        type: 'facetedSelect',
        isAttributeFilter: true,
      },
      options.type.length > 1 && {
        name: 'type',
        label: t('assetType'),
        options: options.type,
        type: 'select',
      },
    ],
    [
      t,
      attributeOptions,
      options.type,
      deploymentOptions.locationNames,
      deploymentOptions.spaceNames,
    ]
  );

  // Memoize the filter value object to prevent recreation
  const filterValue = useMemo(
    () => ({
      searchValue,
      filtersValue,
    }),
    [searchValue, filtersValue]
  );

  // Memoize the onChange callback to prevent recreation
  const handleChange = useCallback(
    ({
      searchValue,
      filtersValue,
    }: {
      searchValue: string;
      filtersValue: URLSearchParams;
    }) => {
      setSearchValue(searchValue);
      setFiltersValue(filtersValue);
      onSubmit({ query: searchValue, filters: parseFilters(filtersValue) });
    },
    [onSubmit]
  );

  return (
    <>
      {!isMobile && (
        <div className='flex items-center gap-1 pr-2.5'>
          <div className='order-1 flex w-6 items-center justify-center'>
            <Checkbox
              value={checkboxState}
              onClick={(_, value) => onCheckboxChange(value)}
            />
          </div>
          <p className='order-2 truncate'>Select All</p>
        </div>
      )}
      <FilterGroupSearch
        filters={filters}
        value={filterValue}
        onChange={handleChange}
        placement={'portal'}
      />
    </>
  );
};

function applyFilters(data: Asset[], filters: ReturnType<typeof parseFilters>) {
  return data.filter((asset) => {
    // Status filter
    if (filters.status && !filters.status.includes(asset.status)) {
      return false;
    }

    // Type filter
    if (filters.type && filters.type.length > 0) {
      // If asset has no type, exclude it when type filter is active
      if (!asset.type) {
        return false;
      }
      // Check if asset type matches any selected filter value
      if (!filters.type.includes(asset.type)) {
        return false;
      }
    }

    // Deployment location filter
    if (filters.deploymentLocation) {
      const deployment = asset.deployment;
      if (
        !deployment ||
        !filters.deploymentLocation.includes(deployment.location.name)
      ) {
        return false;
      }
    }

    // Deployment space filter
    if (filters.deploymentSpace) {
      const deployment = asset.deployment;
      if (
        !deployment ||
        !filters.deploymentSpace.includes(deployment.space.name)
      ) {
        return false;
      }
    }

    return true;
  });
}

export const Assets = () => {
  const [assets, reload, loading] = useAssets();
  const [view, setView] = useState('');
  const [navTypesInNav, setNavTypesInNav] = useState<string[]>([]);
  const { isMobile } = useBreakpoint();
  const outlet = useOutlet();
  const location = useLocation();

  // Selection state for bulk operations
  const [selected, setSelected] = useState<string[]>([]);

  // Track if we're in bulk job creation flow (React-driven flag using ref for synchronous updates)
  // Set when entering bulk job creation, cleared synchronously when leaving
  const isInBulkJobCreationRef = useRef(false);

  // Clear selection when view changes
  useEffect(() => {
    setSelected([]);
  }, [view]);

  // Track bulk job creation flow state based on outlet and navigation state
  // Set flag when entering bulk job creation flow (outlet active + fromBulkAssets in location.state)
  // Clear flag synchronously when outlet closes or when leaving the flow
  useEffect(() => {
    if (outlet && location.state?.fromBulkAssets) {
      // Entering bulk job creation flow - set flag synchronously
      isInBulkJobCreationRef.current = true;
    } else if (outlet && !location.state?.fromBulkAssets) {
      // Outlet opened but not from bulk assets - clear flag
      isInBulkJobCreationRef.current = false;
    } else if (!outlet) {
      // Outlet closed - check if we were in bulk job creation before clearing
      const wasInBulkJobCreation = isInBulkJobCreationRef.current;
      // Clear flag synchronously
      isInBulkJobCreationRef.current = false;

      // Clear selection if:
      // 1. We weren't in bulk job creation flow, OR
      // 2. Job was created successfully (indicated by location.state.jobCreated)
      // Use React-driven flag instead of sessionStorage to avoid race conditions
      if (!wasInBulkJobCreation || location.state?.jobCreated) {
        setSelected([]);
      }
    }
  }, [outlet, location.state?.fromBulkAssets, location.state?.jobCreated]);

  // Stable filter state
  const [filters, setFilters] = useState<ReturnType<typeof parseFilters>>(() =>
    parseFilters(new URLSearchParams([['status', Status.Active]]))
  );

  // Check if we need inactive assets
  const needsInactiveAssets = useMemo(() => {
    return filters.status?.includes(Status.Inactive) ?? false;
  }, [filters.status]);

  // Load inactive assets only when needed
  const [{ data: inactiveData, fetching: inactiveFetching }] =
    useInactiveAssetsQuery({
      pause: !needsInactiveAssets,
    });

  // Merge active and inactive assets when both are needed
  const allAssets = useMemo(() => {
    if (!needsInactiveAssets) {
      return assets;
    }

    const inactiveAssets = inactiveData?.assets || [];
    // Cast inactive assets to Asset type since they have the same structure
    return [...assets, ...(inactiveAssets as Asset[])];
  }, [assets, inactiveData?.assets, needsInactiveAssets]);

  // Use merged assets for search and filtering
  const combinedLoading = loading || (needsInactiveAssets && inactiveFetching);

  // Filter assets by current nav view (All, Other, or a type)
  const baseTypes = useMemo(
    () => navTypesInNav.filter((t) => t !== 'All' && t !== 'Other'),
    [navTypesInNav]
  );
  const assetsByView = useMemo(() => {
    if (!view || view === 'All') return allAssets;
    if (view === 'Other') {
      return allAssets.filter((a) => !a.type || !baseTypes.includes(a.type));
    }
    return allAssets.filter((a) => a.type === view);
  }, [allAssets, view, baseTypes]);

  // Stable search and sort
  const { results, search } = useSearch(assetsByView, SEARCH_OPTIONS);
  const filtered = useMemo(
    () => applyFilters(results || [], filters),
    [results, filters]
  );
  const { sorted } = useSort(filtered, SORT_OPTIONS);
  // Create mutable copy only when sorted changes
  const sortedAssets = useMemo(() => (sorted ? [...sorted] : []), [sorted]);

  // Toggle selection with shift-click support for range selection
  const toggleSelected = useCallback(
    (id: string, shift: boolean, single = false) => {
      if (single) {
        // Single select - used for mobile long press initial selection
        setSelected([id]);
      } else {
        setSelected((prev) => {
          const idx = prev.findIndex((s) => s === id);

          // Shift-click for range selection (desktop)
          if (shift && prev.length > 0) {
            // Find indices in the sorted assets array (visible, filtered list)
            const currentIndex = sortedAssets.findIndex(
              (asset) => asset.id === id
            );
            const lastSelectedIndex = sortedAssets.findIndex(
              (asset) => asset.id === prev[prev.length - 1]
            );

            // Guard: both indices must be found in the visible list, otherwise return unchanged
            if (currentIndex !== -1 && lastSelectedIndex !== -1) {
              const from = Math.min(currentIndex, lastSelectedIndex);
              const to = Math.max(currentIndex, lastSelectedIndex);

              return [...Array(1 + to - from)]
                .map((_, i) => sortedAssets[i + from]?.id)
                .filter(Boolean) as string[];
            }

            // If indices not found in visible list, return previous selection unchanged
            return prev;
          }

          // Toggle selection
          return idx > -1 ? remove(prev, idx) : [...prev, id];
        });
      }
    },
    [sortedAssets]
  );

  // Stable callbacks
  const handleAssetDeactivated = useCallback((assetId: string) => {
    console.log('Asset deactivated:', assetId);
  }, []);

  const handleFilterSubmit = useCallback(
    ({
      query,
      filters,
    }: {
      query: string;
      filters: ReturnType<typeof parseFilters>;
    }) => {
      search(query);
      setFilters(filters);
    },
    [search]
  );

  // Stable context value
  const contextValue = useMemo(
    () => ({
      assets: allAssets,
      reload,
      onAssetDeactivated: handleAssetDeactivated,
    }),
    [allAssets, reload, handleAssetDeactivated]
  );

  // Get selected assets for bulk operations
  const selectedAssets = useMemo(
    () => allAssets.filter((asset) => selected.includes(asset.id)),
    [allAssets, selected]
  );

  // Calculate checkbox state for select all
  const checkboxState = useMemo(
    () =>
      sortedAssets.length === selected.length && sortedAssets.length > 0
        ? CheckboxState.CHECKED
        : selected.length > 0
        ? CheckboxState.INDETERMINATE
        : CheckboxState.UNCHECKED,
    [sortedAssets, selected]
  );

  // Handle select all checkbox change
  const handleCheckboxChange = useCallback(
    (checkboxState: CheckboxState) => {
      const assetIds = sortedAssets.map((asset) => asset.id);
      setSelected(checkboxState === CheckboxState.CHECKED ? assetIds : []);
    },
    [sortedAssets]
  );

  // Determine if we should show mobile selection UI
  const showMobileSelectionUI = isMobile && selected.length > 0;

  // Check if we're in the bulk job creation flow (outlet is showing the job creation form)
  const isInBulkJobCreationFlow =
    outlet && location.state?.fromBulkAssets === true;

  // Show bulk edit view when (matching jobs list pattern):
  // - 2+ assets selected (always show bulk edit, takes precedence over outlet), OR
  // - 1+ assets selected AND no outlet is active (clicked checkbox without navigating)
  // Exception: Don't show bulk edit if we're in bulk job creation flow (need outlet for job form)
  const showBulkEdit =
    !isMobile &&
    !isInBulkJobCreationFlow &&
    (selected.length > 1 || (selected.length > 0 && !outlet));

  // Render main content based on loading/data states
  const renderMainContent = () => {
    // Initial loading state
    if (combinedLoading && allAssets.length === 0) {
      return (
        <div className='flex h-full items-center justify-center'>
          <Loading />
        </div>
      );
    }

    // No assets state
    if (!combinedLoading && sortedAssets.length === 0) {
      return <EmptyListActions listType='Asset' />;
    }

    // Assets loaded state - pass selection props
    return (
      <AssetList
        data={sortedAssets}
        selected={selected}
        toggleSelected={toggleSelected}
      />
    );
  };

  return (
    // Authorization is intentionally disabled for this view while pricing is being implemented.
    /*  <AuthGate action='read' subject='feat.assets'> */
    <AssetContext.Provider value={contextValue}>
      {/* Show AddButton only when not in selection mode */}
      {!showMobileSelectionUI && <AddButton label='Asset' />}

      {/* Mobile selection FAB - shown when assets are selected on mobile */}
      {showMobileSelectionUI && (
        <MobileBulkAssetFAB
          selected={selected}
          selectedAssets={selectedAssets}
          onClearSelection={() => setSelected([])}
          reload={reload}
        />
      )}

      <Split
        nav={
          <AssetsNav
            onViewChange={setView}
            initialView={view}
            onNavItemsChange={setNavTypesInNav}
          />
        }
        filters={
          <AssetFilters
            data={allAssets}
            onSubmit={handleFilterSubmit}
            checkboxState={checkboxState}
            onCheckboxChange={handleCheckboxChange}
            isMobile={isMobile}
          />
        }
        main={renderMainContent()}
        count={sortedAssets.length}
        // Pass custom outlet when showing bulk edit view (matches jobs list behavior)
        // This renders BulkAssetEditView in the same position as the regular Outlet
        customOutlet={
          showBulkEdit ? (
            <BulkAssetEditView
              selected={selected}
              selectedAssets={selectedAssets}
              onClose={() => setSelected([])}
              reload={reload}
            />
          ) : undefined
        }
      />
    </AssetContext.Provider>
    /*  </AuthGate> */
  );
};
